/**
 * Handoff Time Utilities
 *
 * SPEC-2300 collapsed handoff timing to a single 72-hour app-claim window: a row
 * is `pending` and claimable until the hard-expire cron drops it. There is no
 * soft expiry and no extended window, so the only thing the UI still needs is
 * "how long ago did this arrive".
 *
 * All calculations use Date.now() which returns UTC milliseconds since epoch,
 * so comparisons are timezone-agnostic regardless of the user's locale.
 */

// Time constants in milliseconds
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Parse a date value to UTC milliseconds
 * Handles ISO strings, Date objects, and timestamp numbers
 */
export const parseToMs = (dateValue: string | number | Date): number => {
  if (typeof dateValue === 'number') return dateValue;
  if (dateValue instanceof Date) return dateValue.getTime();
  return new Date(dateValue).getTime();
};

/**
 * Format time difference as human-readable "time ago" string
 * @param dateValue - ISO date string, Date, or timestamp
 * @returns e.g., "Just now", "5m ago", "2h ago", "1d ago"
 */
export const formatTimeAgo = (dateValue: string | number | Date): string => {
  const diffMs = Date.now() - parseToMs(dateValue);
  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMs / MS_PER_HOUR);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffMs / MS_PER_DAY);
  return `${diffDays}d ago`;
};
